import { CSSProperties, useMemo, useState } from 'react';
import { ColumnAlingment, ITableDataSet } from '../../interfaces/type1/ITableDataSet';
import './Table.css';

type SortDirection = 'asc' | 'desc';

export default function Table(props: { Data: ITableDataSet }) {
    const [sortColumn, setSortColumn] = useState<number | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const getTextAlign = (i: number): CSSProperties => {
        return props.Data.ColumnsAlingments && props.Data.ColumnsAlingments.Count() > i
            ? {
                textAlign: (() => {
                    switch (props.Data.ColumnsAlingments[i]) {
                        case ColumnAlingment.START: return 'start';
                        case ColumnAlingment.CENTER: return 'center';
                        case ColumnAlingment.END: return 'end';
                    }
                })()
            }
            : {};
    };

    const parseValue = (value: any): number | string => {
        if (value === null || value === undefined)
            return '';

        let text = value.toString().trim();

        if (!text)
            return '';

        // dd/MM/yyyy
        let dateMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (dateMatch) {
            let day = Number(dateMatch[1]);
            let month = Number(dateMatch[2]);
            let year = Number(dateMatch[3]);
            return new Date(year, month - 1, day).getTime();
        }

        // HH:mm
        let hourMatch = text.match(/^(\d{2}):(\d{2})$/);
        if (hourMatch) {
            let hour = Number(hourMatch[1]);
            let minute = Number(hourMatch[2]);
            return hour * 60 + minute;
        }

        // R$ 100.000,00 / 100.000,00 / 100,00 / 100
        let numericText = text
            .replace(/\s/g, '')
            .replace(/^R\$/i, '')
            .replace(/\./g, '')
            .replace(',', '.');

        // 00 min
        let minuteMatch = text.match(/^(\d+(?:,\d+)?)\s*min$/i);
        if (minuteMatch) {
            return Number(minuteMatch[1].replace(',', '.'));
        }

        if (/^-?\d+(\.\d+)?$/.test(numericText))
            return Number(numericText);

        return text.toLocaleLowerCase();
    };

    const compareValues = (a: any, b: any): number => {
        let valueA = parseValue(a);
        let valueB = parseValue(b);

        if (typeof valueA === 'number' && typeof valueB === 'number')
            return valueA - valueB;

        return valueA.toString().localeCompare(valueB.toString(), 'pt-BR');
    };

    const sortedRows = useMemo(() => {
        let rows = props.Data.Rows ? props.Data.Rows.Select(s => s) : [];

        if (sortColumn === null)
            return rows;

        return rows.sort((a, b) => {
            let result = compareValues(a[sortColumn], b[sortColumn]);
            return sortDirection === 'asc' ? result : -result;
        });
    }, [props.Data.Rows, sortColumn, sortDirection]);

    const handleSort = (columnIndex: number) => {
        if (sortColumn === columnIndex) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
            return;
        }

        setSortColumn(columnIndex);
        setSortDirection('asc');
    };

    const getSortIcon = (columnIndex: number) => {
        if (sortColumn !== columnIndex)
            return '';

        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    };

    return (
        <div className='TableComponent'>
            <h1 style={{ textAlign: 'center' }}>Valor tabulado</h1>
            <br />
            <table>
                <thead>
                    <tr>
                        {
                            props.Data.Columns && props.Data.Columns.map((s, i) => (
                                <th
                                    key={i}
                                    style={{ ...getTextAlign(i), cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort(i)}
                                >
                                    {s}{getSortIcon(i)}
                                </th>
                            ))
                        }
                    </tr>
                </thead>

                <tbody>
                    {
                        sortedRows && sortedRows.Select((s) => (
                            <tr key={Math.random()}>
                                {s.map((e, i) => (
                                    <td style={{ ...getTextAlign(i) }} key={i}>
                                        {e}
                                    </td>
                                ))}
                            </tr>
                        ))
                    }

                    {
                        props.Data.Rows && props.Data.Totals &&
                        <tr>
                            {
                                props.Data.Totals.map((s, i) => (
                                    <td
                                        key={i}
                                        className='TotalsRow'
                                        style={{ ...getTextAlign(i) }}
                                    >
                                        {s}
                                    </td>
                                ))
                            }
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    );
}